import { useEffect } from "react";
import { isEmpty } from "lodash";
import { v4 as uuidv4, validate as uuidValidate } from "uuid";

import {
  isPickupAvailableForConsultationType,
  refillOrderTypeFor,
} from "~/shared/utils/sharedUtils";
import KickClient from "~/visits/utils/api.js";
import {
  clearEditPendingVisitSession,
  clearFollowUpVisitFlag,
  clearOrderComplete,
  clearOrderItems,
  clearConsultAnswersJSON,
  clearVisitProduct,
  getIsEditingPendingVisit,
  getVisitID,
  getActiveSubscriptionBySubType,
  setBetaBlockerVariant,
  setConsultationTypeOrderItem,
  setOrderItemSubcategory,
  setEditPendingVisitExpiresAt,
  setEditPendingVisitOriginalOrderItems,
  setEditPendingVisitOriginalVisitID,
  setFollowUpVisitFlag,
  setForceFlavoredBBs,
  setIsEditingPendingVisit,
  setLatestAddress,
  setOrderDetailsData,
  setUserID,
  setVisitEmail,
  setVisitID,
  setVisitPhone,
  setVisitProduct,
  setVisitDifferentPharmacyJSON,
  setIsPickup,
  setConsultationTypeOrderItemAddon,
  setOriginalRefillOrderItems,
  getOrderItemsList,
  setIsEditingPendingVisitPharmacy,
  clearIsEditingPendingVisitPharmacy,
  clearVisitCookies,
  resetCheckoutEditState,
  restoreOtherVisitSessions,
  setUpgradingTo2Pack,
  snapshotVisitSessions,
} from "~/visits/utils/visitCookies";
import {
  consultationTypeForVisitType,
  getBlockingVisitInFamily,
  PENDING_VISIT_EDIT_TTL_MS,
  visitTypeForConsultationType,
} from "~/visits/utils/pendingVisitUtils";

import {
  ATE_SCO,
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY,
  DOCTOR_VISIT_STATUS_CLOSED_COMPLETE,
  DOCTOR_VISIT_STATUS_CLOSED_OTHER,
  DOCTOR_VISIT_STATUS_CLOSED_PAYMENT_UNCOLLECTABLE,
  DOCTOR_VISIT_STATUS_CLOSED_REFERRAL_PRIMARY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_FOLLOW_UP,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_FOLLOW_UP,
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
  SUBSCRIPTION_SUB_TYPE_SLEEP,
  VISIT_TYPE_GENERALIZED_ANXIETY,
  VISIT_TYPE_INSOMNIA,
  VISIT_TYPE_PERFORMANCE_ANXIETY,
} from "~/shared/sharedConstants";

import { GET_PATIENT_RECORD } from "~/patient-portal/components/portalQueries.js";

export function setProperVisitCookies(
  userID,
  visitID,
  orderType,
  email,
  phone,
  address1,
  address2,
  city,
  usState,
  zip,
  checkoutSessionID
) {
  clearConsultAnswersJSON();
  setVisitProduct(orderType, checkoutSessionID);
  setVisitEmail(email);
  clearOrderComplete(checkoutSessionID);

  if (phone) {
    setVisitPhone(phone.replace(/[-()]/g, "").replace(" ", ""));
  }

  setUserID(userID);
  setVisitID(visitID, checkoutSessionID);
  setLatestAddress({
    address1: address1,
    address2: address2,
    city: city,
    usState: usState,
    zip: zip,
  });
}

export function setCookiesAndRedirectToInsomniaFollowUpVisit(
  { id: userID, email, phone },
  { address1, address2, city, usState, zip, pharmacyInfo, orderType }
) {
  const checkoutSessionID = uuidv4();
  clearOrderItems(checkoutSessionID);
  clearIsEditingPendingVisitPharmacy(checkoutSessionID);
  setProperVisitCookies(
    userID,
    "",
    "",
    email,
    phone,
    address1,
    address2,
    city,
    usState,
    zip,
    checkoutSessionID
  );
  let isVisitOnly = orderType.includes("visit-only");
  const activeSubscription = getActiveSubscriptionBySubType(
    SUBSCRIPTION_SUB_TYPE_SLEEP
  );
  if (activeSubscription) {
    isVisitOnly = activeSubscription.orderType.includes("visit-only");
  }
  if (isVisitOnly) {
    setIsPickup(true);
    if (pharmacyInfo && !isEmpty(pharmacyInfo)) {
      setVisitDifferentPharmacyJSON(pharmacyInfo, checkoutSessionID);
    }
    setVisitProduct(
      ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_FOLLOW_UP,
      checkoutSessionID
    );
  } else {
    setVisitProduct(
      ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_FOLLOW_UP,
      checkoutSessionID
    );
  }

  if (!activeSubscription) {
    if (isVisitOnly) {
      setVisitProduct(
        ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
        checkoutSessionID
      );
    } else {
      setVisitProduct(
        ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY,
        checkoutSessionID
      );
    }
  }

  setFollowUpVisitFlag("yes", checkoutSessionID);
  let consultationType = CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP;
  window.location.href = `/visits/${consultationType}/1?checkoutSessionID=${checkoutSessionID}`;
}

export const followUpConsultationTypeFor = (consultationType) => {
  switch (consultationType) {
    case CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY:
      return CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP;
    case CONSULTATION_TYPE_ABBREVIATION_INSOMNIA:
      return CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP;
    case CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY:
      return CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP;
    default:
      return "";
  }
};

function setRefillOrderItems(orderItems, checkoutSessionID) {
  clearOrderItems(checkoutSessionID);
  const addonOrderItems = [];
  const nonAddonOrderItems = [];
  orderItems.forEach((orderItem) => {
    if (orderItem.isAddon) {
      addonOrderItems.push(orderItem);
    } else {
      nonAddonOrderItems.push(orderItem);
    }
  });

  (nonAddonOrderItems || []).forEach((orderItem) => {
    const { consultationType, productSlug, subcategory } = orderItem;
    const followUpConsultationType =
      followUpConsultationTypeFor(consultationType) || consultationType;
    const refillProductSlug = refillOrderTypeFor(productSlug) || productSlug;
    setConsultationTypeOrderItem(
      followUpConsultationType,
      refillProductSlug,
      checkoutSessionID
    );
    if (subcategory) {
      setOrderItemSubcategory(
        followUpConsultationType,
        subcategory,
        checkoutSessionID
      );
    }
  });

  (addonOrderItems || []).forEach((orderItem) => {
    const { consultationType, productSlug, subcategory } = orderItem;
    const followUpConsultationType =
      followUpConsultationTypeFor(consultationType) || consultationType;
    const refillProductSlug = refillOrderTypeFor(productSlug) || productSlug;
    setConsultationTypeOrderItemAddon(
      followUpConsultationType,
      refillProductSlug,
      subcategory,
      checkoutSessionID
    );
  });

  setOriginalRefillOrderItems(
    getOrderItemsList(checkoutSessionID),
    checkoutSessionID
  );
}

// refillDisplayOrderItemsFor maps a visit's order items to their refill
// equivalents for DISPLAY (same mapping setRefillOrderItems applies when
// stamping, but with no session side effects) — used by the refill-make-changes
// selector to render each cross-visit option before it is stamped. Visits
// without orderItems (older data) fall back to a single row derived from the
// visit's orderType.
export function refillDisplayOrderItemsFor(visit, fallbackConsultationType) {
  const mapItem = ({
    consultationType,
    productSlug,
    subcategory,
    isAddon,
  }) => ({
    consultationType:
      followUpConsultationTypeFor(consultationType) || consultationType,
    productSlug: refillOrderTypeFor(productSlug) || productSlug,
    subcategory: subcategory || "",
    isAddon: !!isAddon,
  });

  if (visit?.orderItems?.length) {
    return visit.orderItems.map(mapItem);
  }
  if (!visit?.orderType) return [];
  return [
    {
      consultationType: fallbackConsultationType,
      productSlug: refillOrderTypeFor(visit.orderType) || visit.orderType,
      subcategory: "",
      isAddon: false,
    },
  ];
}

export function setProperVisitCookiesForRefills(
  { id: userID, email, phone, address1, address2, city, usState, zip }, // patient
  { id: visitID, orderType: originalVisitOrderType, orderItems }, // visit
  { medicationName, medicationDosage }, // prescription
  checkoutSessionID,
  // preserveOtherSessions: for MID-FLOW re-stamps (the refill picker switching
  // visits) — snapshots the other checkout sessions + consult answers that
  // clearVisitCookies() would otherwise destroy, and restores them after the
  // stamp. Flow-START stamps keep the full wipe.
  { preserveOtherSessions = false } = {}
) {
  const preserved = preserveOtherSessions ? snapshotVisitSessions() : null;
  clearVisitCookies();
  clearConsultAnswersJSON();

  // Stamp the product from the primary order item's slug when present — the
  // refill picker labels and dedupes options by item slug, and a visit's
  // orderType can drift from its items after doctor edits. Fall back to the
  // visit's orderType for older visits without orderItems.
  const primaryOrderItem = (orderItems || []).find((oi) => oi && !oi.isAddon);
  const primarySlug = primaryOrderItem?.productSlug || originalVisitOrderType;
  const refillOrderType = refillOrderTypeFor(primarySlug) || primarySlug;
  setBetaBlockerVariant(
    `${medicationName} ${medicationDosage}`,
    checkoutSessionID
  );
  setProperVisitCookies(
    userID,
    visitID,
    refillOrderType,
    email,
    phone,
    address1,
    address2,
    city,
    usState,
    zip,
    checkoutSessionID
  );
  // Older visits may not have orderItems
  if (orderItems?.length) {
    setRefillOrderItems(orderItems, checkoutSessionID);
  }
  if (preserved) {
    restoreOtherVisitSessions(preserved, checkoutSessionID);
  }
}

export function setCookiesAndRedirectToPerformanceFollowUpVisit(
  patient,
  prescription,
  productType = null,
  upgradingTo2Pack = false
) {
  const checkoutSessionID = uuidv4();
  setProperVisitCookiesForFollowUp(patient, prescription, checkoutSessionID);
  const consultationType =
    CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP;

  if (productType) {
    setVisitProduct(productType, checkoutSessionID);
    setConsultationTypeOrderItem(
      consultationType,
      productType,
      checkoutSessionID
    );
  }

  if (upgradingTo2Pack) {
    setUpgradingTo2Pack(true, checkoutSessionID);
  }

  window.location.href = `/visits/${consultationType}/1?checkoutSessionID=${checkoutSessionID}`;
}

export function setProperVisitCookiesForFollowUp(
  { id: userID, email, phone, address1, address2, city, usState, zip },
  { medicationName, medicationDosage },
  checkoutSessionID
) {
  clearOrderItems(checkoutSessionID);
  clearIsEditingPendingVisitPharmacy(checkoutSessionID);

  setFollowUpVisitFlag("yes", checkoutSessionID);
  setBetaBlockerVariant(
    `${medicationName} ${medicationDosage}`,
    checkoutSessionID
  );
  setProperVisitCookies(
    userID,
    "",
    "",
    email,
    phone,
    address1,
    address2,
    city,
    usState,
    zip,
    checkoutSessionID
  );
}

export function saveConsultIntake(visitID, consultationType, intake) {
  const path = `/visits/${visitID}/consult-intakes`;
  KickClient.newAuthenticatedClient()
    .post(path, {
      consultationType,
      intake,
    })
    .then((response) => {
      console.log(response);
    })
    .catch((error) => {
      console.error("[saveConsultIntake] Error: ", error);
    });
}

export function useVisitIDRequired(checkoutSessionID) {
  let poller;
  useEffect(() => {
    poller = setInterval(() => {
      const visitID = getVisitID(checkoutSessionID);
      if (!visitID) {
        window.location.href = "/me/portal";
        clearInterval(poller);
      }
    }, 1000);
    return () => clearInterval(poller);
  }, [checkoutSessionID]);
}

export function setCookiesAndRedirectToEditPharmacy(visit) {
  const checkoutSessionID = uuidv4();
  setVisitDifferentPharmacyJSON(visit.pharmacyInfo, checkoutSessionID);
  setVisitID(visit.id, checkoutSessionID);
  setIsEditingPendingVisitPharmacy(true, checkoutSessionID);
  window.location.href = `/visits/edit/select-pharmacy?checkoutSessionID=${checkoutSessionID}`;
}

// setCookiesAndRedirectToEditShippingAddress drops the patient into the existing
// confirm-shipping-details page to edit ONLY the shipping address of a pending mail-order visit
// (used for pending INF visits, where the full order-details edit flow doesn't apply). Mirrors
// setCookiesAndRedirectToEditPharmacy: it reuses the isEditingPendingVisitPharmacy flag so the
// page saves via PUT /update-shipping-address and bounces back to /me/portal instead of
// continuing the new-visit flow. setVisitProduct is required so confirmShippingDetails' mail-order
// supported-state validation runs.
export function setCookiesAndRedirectToEditShippingAddress(visit) {
  const checkoutSessionID = uuidv4();
  setVisitID(visit.id, checkoutSessionID);
  if (visit.orderType) {
    setVisitProduct(visit.orderType, checkoutSessionID);
  }
  // Prefill the address form. confirmShippingDetails reads getOrderDetailsData first and resolves
  // usState via usStatesList.find on the abbreviation, so pass usState as the abbreviation string.
  setOrderDetailsData(
    {
      address1: visit.address1,
      address2: visit.address2,
      city: visit.city,
      usState: visit.usState,
      zip: visit.zip,
    },
    checkoutSessionID
  );
  setIsEditingPendingVisitPharmacy(true, checkoutSessionID);
  const consultationType = consultationTypeForVisitType(visit.visitType);
  window.location.href = `/visits/${consultationType}/confirm-shipping-details?checkoutSessionID=${checkoutSessionID}`;
}

// setCookiesAndRedirectToEditPendingVisit seeds a fresh checkoutSession from the existing pending
// visit and drops the patient into the visit flow at /select-pharmacy. The downstream pages
// (num-packs, flavor pickers, order-details) reuse the same cookies as a normal visit; the
// `isEditingPendingVisit` flag is what makes order-details PATCH the existing visit at the end
// instead of POSTing a new one. Calls the backend lock endpoint synchronously — if it returns 409,
// surface an alert and abort.
export async function setCookiesAndRedirectToEditPendingVisit(
  visit,
  patient,
  { forceFlavored = false, forceMailOrder = false } = {}
) {
  // Start every edit from a clean slate so a previously-abandoned edit (e.g. one switched to local
  // pickup) can't leak its fulfillment/pharmacy choice into this session. This guarantees the reset
  // even when the patient returned to the portal via browser-back (which may skip the portal's
  // mount-time reset).
  resetCheckoutEditState();
  const checkoutSessionID = uuidv4();

  setProperVisitCookies(
    patient.id,
    visit.id,
    visit.orderType || "",
    patient.email,
    patient.phone,
    // Seed the address from the VISIT being edited so /order-details prefills
    // the current visit's shipping address. (The GraphQL patient record has no
    // address fields, so patient.address* would be undefined here.)
    visit.address1,
    visit.address2,
    visit.city,
    visit.usState,
    visit.zip,
    checkoutSessionID
  );

  setVisitID(visit.id, checkoutSessionID);
  if (visit.orderType) {
    setVisitProduct(visit.orderType, checkoutSessionID);
  }

  (visit.orderItems || []).forEach((oi) => {
    if (!oi.consultationType || !oi.productSlug) return;
    if (oi.isAddon) {
      setConsultationTypeOrderItemAddon(
        oi.consultationType,
        oi.productSlug,
        oi.subcategory || "",
        checkoutSessionID
      );
    } else {
      setConsultationTypeOrderItem(
        oi.consultationType,
        oi.productSlug,
        checkoutSessionID
      );
      if (oi.subcategory) {
        setOrderItemSubcategory(
          oi.consultationType,
          oi.subcategory,
          checkoutSessionID
        );
      }
    }
  });

  const consultationType = consultationTypeForVisitType(visit.visitType);

  // GA/GAF visits are mail-order only (compounded meds, no pickup SKU): never seed pickup state
  // for them, even if the persisted visit already carries a bad visit-only order type or a
  // pickup pharmacy (self-heals the incident shape on the next edit).
  const isPickupVisit =
    !forceMailOrder &&
    isPickupAvailableForConsultationType(consultationType) &&
    (visit.orderType?.includes("visit-only") ||
      (visit.pharmacyInfo && !isEmpty(visit.pharmacyInfo)));

  // isPickup is a global cookie (not per-session), so it must be set explicitly for
  // BOTH branches — otherwise a stale value from a previous edit session leaks in
  // (e.g. editing a visit-only sleep visit, then a mail-order PA visit, would keep
  // the pickup view on /select-pharmacy).
  if (isPickupVisit) {
    setIsPickup(true);
    if (visit.pharmacyInfo) {
      setVisitDifferentPharmacyJSON(visit.pharmacyInfo, checkoutSessionID);
    }
  } else {
    setIsPickup(false);
  }

  if (forceFlavored) {
    setForceFlavoredBBs(true, checkoutSessionID);
  }

  setIsEditingPendingVisit(true, checkoutSessionID);
  setEditPendingVisitOriginalVisitID(visit.id, checkoutSessionID);
  // Capture the original order items so the flavor screens can preselect the
  // patient's existing choices even after in-flow screens wipe the live cookie.
  setEditPendingVisitOriginalOrderItems(visit.orderItems || [], checkoutSessionID);
  setEditPendingVisitExpiresAt(
    Date.now() + PENDING_VISIT_EDIT_TTL_MS,
    checkoutSessionID
  );

  try {
    await KickClient.newAuthenticatedClient().post(
      "/visits/edit-pending/lock",
      { visit_id: visit.id }
    );
  } catch (err) {
    const code = err?.response?.data?.code;
    if (code === "visit_not_editable") {
      alert(
        "This visit can no longer be edited. Please refresh the page to see its current status."
      );
    } else {
      alert("Sorry, we couldn't start an edit session. Please try again.");
    }
    clearEditPendingVisitSession(checkoutSessionID);
    return;
  }

  // GA/GAF edits skip /select-pharmacy entirely: that page's pickup path is what rewrote a GAF
  // visit's order type to prop-visit-only. Their real first page (matching the checkout flow in
  // consultationQuestionnaire) is the ate-sco product picker.
  if (!isPickupAvailableForConsultationType(consultationType)) {
    window.location.href = `/visits/${consultationType}/select-ga-product?checkoutSessionID=${checkoutSessionID}`;
    return;
  }
  window.location.href = `/visits/${consultationType}/select-pharmacy?checkoutSessionID=${checkoutSessionID}`;
}

export function ensureCheckoutSessionID(checkoutSessionID, history) {
  // if checkoutSessionID is a uuid, then return it, otherwise create a random one and add it to the query string
  if (uuidValidate(checkoutSessionID)) {
    return true;
  }
  const path = window.location.pathname;
  const newCheckoutSessionID = uuidv4();
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.set("checkoutSessionID", newCheckoutSessionID);
  const newPath = path + "?" + searchParams.toString() + window.location.hash;
  // history.push(newPath);
  // need hard reload
  window.location.href = newPath;
  return false;
}

/**
 * Checks if a user should be redirected to a follow-up flow based on their visit history.
 * - For "pac" or "paf" users with completed scopolamine orders, redirects to "gaf"
 * - For initial visit types with completed initial visits, redirects to follow-up
 *
 * @param {Object} apolloClient - Apollo client instance for GraphQL queries
 * @param {string} consultationType - The current consultation type abbreviation
 * @param {string} checkoutSessionID - The checkout session ID
 * @param {Object} options - Optional configuration
 * @param {boolean} options.redirectToPortal - If true, redirects to /me/portal instead of follow-up questionnaire
 * @returns {Promise<boolean>} - Returns true if a redirect was triggered, false otherwise
 */
export async function checkAndRedirectToFollowUpIfNeeded(
  apolloClient,
  consultationType,
  checkoutSessionID,
  options = {}
) {
  // When the patient is in an active "Edit Pending Visit" flow, do NOT bounce them around to a
  // different consultation type — we are editing a specific pre-existing visit.
  if (checkoutSessionID && getIsEditingPendingVisit(checkoutSessionID)) {
    return false;
  }

  const { redirectToPortal = false, preloadedVisits = null } = options;

  const redirectConfig = {
    [CONSULTATION_TYPE_ABBREVIATION_INSOMNIA]: {
      visitType: VISIT_TYPE_INSOMNIA,
      followUpPath: CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP,
    },
    [CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY]: {
      visitType: VISIT_TYPE_PERFORMANCE_ANXIETY,
      followUpPath: CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
    },
  };

  const config = redirectConfig[consultationType];

  // Check if this is a "pac" or "paf" consultation that might need "gaf" redirect
  const isPacOrPaf =
    consultationType === CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY ||
    consultationType === CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP;

  // If no config and not "pac" or "paf", nothing to check
  if (!config && !isPacOrPaf) return false;

  try {
    // Reuse the caller's already-fetched visits when provided (the questionnaire mount runs the
    // pending-visit guard first, which fetches the same GET_PATIENT_RECORD) — avoids a second
    // identical network-only round-trip.
    let visits = preloadedVisits;
    if (!visits) {
      const { data } = await apolloClient.query({
        query: GET_PATIENT_RECORD,
        fetchPolicy: "network-only",
      });
      visits = data?.patientRecord?.visits;
    }

    if (visits) {
      // Check for "pac" or "paf" users with completed scopolamine orders - redirect to "gaf"
      if (isPacOrPaf) {
        const hasCompletedScopolamineVisit = visits.some(
          (visit) =>
            visit.status === DOCTOR_VISIT_STATUS_CLOSED_COMPLETE &&
            visit.orderType?.includes(ATE_SCO)
        );

        if (hasCompletedScopolamineVisit) {
          if (redirectToPortal) {
            window.location.href = "/me/portal";
          } else {
            window.location.href = `/visits/${CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP}/1?checkoutSessionID=${checkoutSessionID}`;
          }
          return true;
        }
      }

      // Check for completed initial visit - redirect to follow-up
      if (config) {
        const hasCompletedInitialVisit = visits.some(
          (visit) =>
            visit.visitType === config.visitType &&
            visit.status === DOCTOR_VISIT_STATUS_CLOSED_COMPLETE
        );

        if (hasCompletedInitialVisit) {
          if (redirectToPortal) {
            window.location.href = "/me/portal";
          } else {
            window.location.href = `/visits/${config.followUpPath}/1?checkoutSessionID=${checkoutSessionID}`;
          }
          return true;
        }
      }
    }
  } catch (error) {
    // Log error but don't block the user from continuing
    console.error("Error checking for follow-up redirect:", error);
  }

  return false;
}

// Consultation-type abbreviation of the INITIAL funnel for each FOLLOW-UP
// abbreviation. GA initial currently uses the PA-initial questionnaire, so gaf
// steers to pac. Mirror of the backend's RequiredInitialVisitTypesForFollowUp.
export const INITIAL_CONSULTATION_TYPE_FOR_FOLLOW_UP = {
  [CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP]:
    CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY,
  [CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP]:
    CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY,
  [CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP]:
    CONSULTATION_TYPE_ABBREVIATION_INSOMNIA,
};

// Visit types that qualify as the prior INITIAL visit for each FOLLOW-UP
// abbreviation (gaf accepts GA or PA, mirroring the backend fallback).
const QUALIFYING_INITIAL_VISIT_TYPES_FOR_FOLLOW_UP = {
  [CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP]: [
    VISIT_TYPE_PERFORMANCE_ANXIETY,
  ],
  [CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP]: [
    VISIT_TYPE_GENERALIZED_ANXIETY,
    VISIT_TYPE_PERFORMANCE_ANXIETY,
  ],
  [CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP]: [VISIT_TYPE_INSOMNIA],
};

// Statuses that make an initial visit count as a qualifying prior (matches the
// backend guard: completed, or closed uncollectable/referral-primary/other).
const QUALIFYING_INITIAL_VISIT_STATUSES = [
  DOCTOR_VISIT_STATUS_CLOSED_COMPLETE,
  DOCTOR_VISIT_STATUS_CLOSED_PAYMENT_UNCOLLECTABLE,
  DOCTOR_VISIT_STATUS_CLOSED_REFERRAL_PRIMARY,
  DOCTOR_VISIT_STATUS_CLOSED_OTHER,
];

// hasQualifyingPriorInitialVisit reports whether the visit list contains a
// qualifying prior INITIAL visit for the given follow-up consultation type.
// Returns true for non-follow-up consultation types (nothing to require).
export function hasQualifyingPriorInitialVisit(visits, consultationType) {
  const qualifyingTypes =
    QUALIFYING_INITIAL_VISIT_TYPES_FOR_FOLLOW_UP[consultationType];
  if (!qualifyingTypes) return true;
  return (visits || []).some(
    (visit) =>
      qualifyingTypes.includes(visit.visitType) &&
      QUALIFYING_INITIAL_VISIT_STATUSES.includes(visit.status)
  );
}

/**
 * Steers the user out of a FOLLOW-UP funnel into question 1 of the matching
 * INITIAL funnel, clearing the follow-up session state that would otherwise
 * leak: the follow-up flag re-maps product slugs to follow-up SKUs mid-funnel,
 * and portal entry links stamp a follow-up SKU into visitProduct — both must
 * go so the initial funnel prices and submits as an initial visit.
 *
 * @param {string} consultationType - The FOLLOW-UP consultation type abbreviation
 * @param {string} checkoutSessionID - The checkout session ID (kept across the redirect)
 * @returns {boolean} - true if a redirect was triggered (false for non-follow-up types)
 */
export function redirectToInitialFunnel(consultationType, checkoutSessionID) {
  const initialConsultationType =
    INITIAL_CONSULTATION_TYPE_FOR_FOLLOW_UP[consultationType];
  if (!initialConsultationType) return false;
  if (checkoutSessionID) {
    clearFollowUpVisitFlag(checkoutSessionID);
    clearVisitProduct(checkoutSessionID);
  }
  window.location.href = `/visits/${initialConsultationType}/1?checkoutSessionID=${checkoutSessionID}`;
  return true;
}

/**
 * Redirects a user out of a FOLLOW-UP funnel into the matching INITIAL funnel
 * when they have no qualifying prior initial visit — the mirror image of
 * checkAndRedirectToFollowUpIfNeeded. Fails open on query errors or an
 * anonymous session: the server guard on the questionnaire POST is
 * authoritative; this just spares the user filling a form that will 409.
 *
 * @param {Object} apolloClient - Apollo client instance for GraphQL queries
 * @param {string} consultationType - The current consultation type abbreviation
 * @param {string} checkoutSessionID - The checkout session ID
 * @param {Object} options - { preloadedVisits }: reuse the caller's
 *   already-fetched patientRecord visits (the questionnaire mount runs the
 *   pending-visit guard first, which fetches the same GET_PATIENT_RECORD) —
 *   avoids another identical network-only round-trip.
 * @returns {Promise<boolean>} - Returns true if a redirect was triggered
 */
export async function checkAndRedirectToInitialIfNoHistory(
  apolloClient,
  consultationType,
  checkoutSessionID,
  { preloadedVisits = null } = {}
) {
  // Never bounce an active "Edit Pending Visit" session to a different funnel.
  if (checkoutSessionID && getIsEditingPendingVisit(checkoutSessionID)) {
    return false;
  }

  const initialConsultationType =
    INITIAL_CONSULTATION_TYPE_FOR_FOLLOW_UP[consultationType];
  if (!initialConsultationType) return false;

  try {
    let visits = preloadedVisits;
    if (!visits) {
      const { data } = await apolloClient.query({
        query: GET_PATIENT_RECORD,
        fetchPolicy: "network-only",
      });
      visits = data?.patientRecord?.visits;
    }
    // Only act on a definitive read of the visit list; fail open otherwise.
    if (!visits) return false;
    if (hasQualifyingPriorInitialVisit(visits, consultationType)) {
      return false;
    }
    return redirectToInitialFunnel(consultationType, checkoutSessionID);
  } catch (error) {
    console.error("Error checking for initial-visit history:", error);
  }

  return false;
}

// checkAndRedirectIfPendingVisitOfTypeExists redirects the patient back to /me/portal (with a
// ?notice=pending_visit banner flag) if they already have a BLOCKING in-progress visit (status
// P/R/PRAD/PR) in the same clinical family (anxiety or sleep) as the consultation they are
// trying to start — i.e. a second order in a vertical while the first is queued for or mid
// clinical review. A visit stalled in Pending Verification (PV) does NOT redirect: the patient
// abandoned the ID-verification step, so they may start over — the new checkout supersedes the
// PV visit (the backend closes it as duplicate; see CompleteCheckout on the Go side).
// Excludes the flow's own visit: the INF shortcut creates it in status "P" mid-flow, which would
// otherwise self-match and eject the patient on back/reload. No-op when the patient is in an
// active "Edit Pending Visit" flow. The backend enforces the same rule at /complete-checkout for
// any path that skips this check.
//
// Returns { redirected, visits, subscriptions }: `visits` and `subscriptions` carry the fetched
// patientRecord data (null when the fetch was skipped or failed) so the caller can reuse them
// instead of refetching the same query.
export async function checkAndRedirectIfPendingVisitOfTypeExists(
  apolloClient,
  consultationType,
  checkoutSessionID
) {
  if (checkoutSessionID && getIsEditingPendingVisit(checkoutSessionID)) {
    return { redirected: false, visits: null, subscriptions: null };
  }

  const targetVisitType = visitTypeForConsultationType(consultationType);
  if (!targetVisitType)
    return { redirected: false, visits: null, subscriptions: null };

  try {
    const { data } = await apolloClient.query({
      query: GET_PATIENT_RECORD,
      fetchPolicy: "network-only",
    });
    const visits = data?.patientRecord?.visits || null;
    const subscriptions = data?.patientRecord?.subscriptions || null;
    if (!visits) return { redirected: false, visits: null, subscriptions };
    // The flow's own visit (recorded on the checkout session once the intake is saved) must not
    // trigger the redirect.
    const currentVisitID = checkoutSessionID
      ? getVisitID(checkoutSessionID)
      : null;
    const pending = getBlockingVisitInFamily(
      visits,
      targetVisitType,
      currentVisitID
    );
    if (pending) {
      window.location.href = "/me/portal?notice=pending_visit";
      return { redirected: true, visits, subscriptions };
    }
    return { redirected: false, visits, subscriptions };
  } catch (err) {
    console.error("Error checking for pending visit redirect:", err);
  }
  return { redirected: false, visits: null, subscriptions: null };
}
